import moment from 'moment';
import DatePicker from 'react-datepicker';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../../../hooks/useAuth';
import { useEnv } from '../../../../context/env-context';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';
import { makeRequest } from '../../../../utils/makeRequest';
import { REWARDS_CATEGORIES } from '../../../../constants/consumers';

function ReportsRewardsLogModalForm({
  close,
  setSpinner,
  createReport,
  aggregators,
  getAggregators,
}) {
  const { apiOriginConsumer } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const [surveyTypes, setSurveyTypes] = useState([]);

  const [aggregatorId, setAggregatorId] = useState('');
  const [asOfDate, setAsOfDate] = useState(moment().format('YYYY-MM-DD'));
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedType, setSelectedType] = useState('');

  const getSurveyTypes = useCallback(
    async category => {
      const token = await getAccessTokenSilently();

      if (!token) {
        return;
      }

      setSpinner(true);
      const config = {
        token,
        url: `${apiOriginConsumer}/rewards/types`,
        method: 'GET',
        params: { category },
      };

      try {
        const response = await makeRequest(config);

        setSurveyTypes(response);
      } catch (error) {
        toast.error(`Failed getting survey types: ${error.message}`);
      } finally {
        setSpinner(false);
      }
    },
    [apiOriginConsumer, getAccessTokenSilently, setSpinner],
  );

  useEffect(() => {
    (async () => {
      setSpinner(true);
      await getAggregators();
      setSpinner(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      getSurveyTypes(selectedCategory);
    }
  }, [selectedCategory, getSurveyTypes]);

  const handleCreate = () =>
    createReport({
      aggregatorId: aggregatorId ? Number(aggregatorId) : null,
      date: asOfDate,
      category: selectedCategory || null,
      type: selectedType || null,
    });

  return (
    <>
      <div>
        <Row className="align-items-center">
          <Col md={4} className="bo-same-line">
            <Form.Label>Aggregator:</Form.Label>
          </Col>
          <Col md={8} className="bo-same-line">
            <Form.Select
              className="bo-filter bo-w-250"
              value={aggregatorId}
              onChange={e => setAggregatorId(e.target.value)}
            >
              <option value="">All Standard Aggregators</option>
              {aggregators.map(agg => (
                <option key={agg.id} value={agg.id}>
                  {agg.name}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>
        <Row className="mt-2 mb-2 align-items-center">
          <Col md={4} className="bo-same-line">
            <Form.Label>Date:</Form.Label>
          </Col>
          <Col md={8} className="bo-same-line">
            <DatePicker
              className="bo-input bo-date-picker bo-w-250"
              onChange={e => setAsOfDate(moment(e).format('YYYY-MM-DD'))}
              selected={new Date(asOfDate)}
              dateFormat="yyyy-MM-dd"
              customInput={<Form.Control />}
            />
          </Col>
        </Row>
        <Row className="mt-2 mb-2 align-items-center">
          <Col md={4} className="bo-same-line">
            <Form.Label>Category:</Form.Label>
          </Col>
          <Col md={8} className="bo-same-line">
            <Form.Select
              className="bo-filter bo-w-250"
              value={selectedCategory}
              onChange={e => {
                setSelectedCategory(e.target.value);
                setSelectedType('');
              }}
            >
              <option value="">All Categories</option>
              {REWARDS_CATEGORIES.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>
        <Row className="mt-2 mb-2 align-items-center">
          <Col md={4} className="bo-same-line">
            <Form.Label>Reward Type:</Form.Label>
          </Col>
          <Col md={8} className="bo-same-line">
            <Form.Select
              className="bo-filter bo-w-250"
              value={selectedType}
              onChange={e => setSelectedType(e.target.value)}
              disabled={!selectedCategory}
            >
              <option value="">All Reward Types</option>
              {selectedCategory &&
                surveyTypes.length > 0 &&
                surveyTypes.map(survey => (
                  <option key={survey.type} value={survey.type}>
                    {survey.name.en}
                  </option>
                ))}
            </Form.Select>
          </Col>
        </Row>
      </div>
      <Row className="mt-auto">
        <Col className="d-flex justify-content-center gap-3">
          <Button className="bo-button" onClick={handleCreate}>
            Create
          </Button>
          <Button className="bo-button" onClick={close}>
            Cancel
          </Button>
        </Col>
      </Row>
    </>
  );
}

export default ReportsRewardsLogModalForm;
